import { AdminDashboardContext } from './contexts';
import { Box, Button, Tab, Typography } from '@mui/material';
import { TeamAvailability } from '../components/TeamAvailability';
import { theme } from '@store';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Select, toolbarFieldSx } from '@ui';
import { ChartsTimeInterval, chartTimeIntervalOptions } from '../types';
import dayjs from 'dayjs';
import { useAdminView } from './hooks';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DiamondIcon from '@mui/icons-material/Diamond';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import ChromeReaderModeIcon from '@mui/icons-material/ChromeReaderMode';
import { PatientCharts } from './PatientCharts';
import { TaskCharts } from './TaskCharts';
import { ProgramCharts } from './ProgramCharts';
import { t } from 'i18next';
import { MultiselectSearchableField } from 'components/Multiselect';

// Filter fields grow into the free space of the row but never shrink below a
// legible width — the row wraps instead of squeezing them.
const filterFieldSx = {
  ...toolbarFieldSx,
  overflow: 'visible',
};

export const AdminView = () => {
  const {
    user,
    viewTabIndex,
    handleViewTabChange,
    entityTabIndex,
    handleEntityTabChange,
    filters,
    handleFilterChange,
    // roleList,
    // teamList,
    // userList,
    supervisorOptions,
    partnerOptions,
    handleResetFilters,
    canResetFilters,
  } = useAdminView();

  const contextValues = {
    viewTabIndex,
    handleViewTabChange,
    entityTabIndex,
    handleEntityTabChange,
    filters,
    handleFilterChange,
  };

  return (
    <AdminDashboardContext.Provider value={contextValues}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          gap: '1rem',
          justifyContent: 'start',
          '& > *': {
            overflow: 'visible',
          },
          [theme.breakpoints.down('md')]: {
            padding: '0',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='h4'
            sx={{ textAlign: 'start', width: '100%', mt: 2, mb: 2 }}
          >
            Hello, {user?.first_name}
          </Typography>

          <MultiselectSearchableField
            options={partnerOptions}
            value={Array.isArray(filters.record_ids) ? filters?.record_ids : []}
            name={'record_ids'}
            label={'Partner'}
            handleChange={values => handleFilterChange({ record_ids: values })}
            optional
            sx={{
              bgcolor: theme.palette.common.white,
              ...filterFieldSx,
            }}
          />

          <Box component='nav' sx={{ display: 'none' }}>
            <Box
              sx={{
                cursor: 'pointer',
                height: '48px',
                width: '120px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                border: theme => `1px solid ${theme.palette.grey[300]}`,
                borderBottomLeftRadius: '4px',
                borderTopLeftRadius: '4px',
                background: theme =>
                  viewTabIndex === '1'
                    ? theme.palette.selectedPageBg.main
                    : 'unset',
              }}
              onClick={() => handleViewTabChange('1')}
            >
              <AccountCircleIcon sx={{ height: '24px' }} color='primary' />
              <Typography variant='body2'>USERS</Typography>
            </Box>
            <Box
              sx={{
                cursor: 'pointer',
                height: '48px',
                width: '120px',
                // display: 'flex',
                display: 'none',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                border: theme => `1px solid ${theme.palette.grey[300]}`,
                borderBottomRightRadius: '4px',
                borderTopRightRadius: '4px',
                background: theme =>
                  viewTabIndex === '2'
                    ? theme.palette.selectedPageBg.main
                    : 'unset',
              }}
              onClick={() => handleViewTabChange('2')}
            >
              <DiamondIcon sx={{ height: '24px' }} color='primary' />
              <Typography variant='body2'>TEAMS</Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            gap: 3,
            width: '100%',
            mb: '2rem',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
            }}
          >
            {/* /Filters */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                minWidth: 0,
              }}
            >
              {
                <>
                  <MultiselectSearchableField
                    options={supervisorOptions}
                    value={
                      Array.isArray(filters.supervisor_ids)
                        ? filters?.supervisor_ids
                        : []
                    }
                    name={'supervisor_ids'}
                    label={'Supervisor'}
                    handleChange={values =>
                      handleFilterChange({ supervisor_ids: values })
                    }
                    optional
                    sx={{
                      bgcolor: theme.palette.common.white,
                      ...filterFieldSx,
                    }}
                  />
                </>
              }

              <Select
                name='interval'
                clearable={false}
                options={chartTimeIntervalOptions}
                value={filters?.interval}
                label='Time range'
                sx={{
                  bgcolor: theme.palette.common.white,
                  ...filterFieldSx,
                }}
                onChange={event => {
                  handleFilterChange({
                    interval: event.target.value as ChartsTimeInterval,
                  });
                }}
              />
              {filters?.interval === 'custom' && (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale='en'
                >
                  <DatePicker
                    name='from_date'
                    label='From'
                    value={filters?.from_date ? dayjs(filters.from_date) : null}
                    onChange={val => {
                      const formattedValue = val?.isValid()
                        ? dayjs(val).format('YYYY-MM-DD')
                        : '';

                      handleFilterChange({
                        from_date: formattedValue,
                      });
                    }}
                    sx={filterFieldSx}
                  />
                  <DatePicker
                    name='to_date'
                    label='To'
                    value={filters.to_date ? dayjs(filters.to_date) : null}
                    minDate={dayjs(filters.from_date).add(1, 'day')}
                    onChange={val => {
                      const formattedValue = val?.isValid()
                        ? dayjs(val).format('YYYY-MM-DD')
                        : '';

                      handleFilterChange({
                        to_date: formattedValue,
                      });
                    }}
                    sx={filterFieldSx}
                  />
                </LocalizationProvider>
              )}
              <Button
                variant='text'
                onClick={handleResetFilters}
                disabled={!canResetFilters}
                sx={{ flexShrink: 0 }}
              >
                {t('general.reset')}
              </Button>
            </Box>

            {/* Tabs */}
            <TabContext value={entityTabIndex}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }}>
                <TabList onChange={handleEntityTabChange}>
                  <Tab
                    iconPosition='start'
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <AssignmentIcon /> TASKS
                      </Box>
                    }
                    value='1'
                  />
                  <Tab
                    iconPosition='start'
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <PeopleIcon /> PATIENTS
                      </Box>
                    }
                    value='2'
                  />
                  <Tab
                    iconPosition='start'
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <ChromeReaderModeIcon /> PROGRAMS
                      </Box>
                    }
                    value='3'
                  />
                </TabList>
              </Box>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  maxHeight: 'calc(100vh - 330px)',
                  overflowY: 'auto',
                }}
                value='1'
              >
                <TaskCharts />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  maxHeight: 'calc(100vh - 330px)',
                  overflowY: 'auto',
                }}
                value='2'
              >
                <PatientCharts />
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                  maxHeight: 'calc(100vh - 330px)',
                  overflowY: 'auto',
                }}
                value='3'
              >
                <ProgramCharts />
              </TabPanel>
            </TabContext>
          </Box>
          <TeamAvailability />
        </Box>
      </Box>
    </AdminDashboardContext.Provider>
  );
};
